import { useId } from 'react';
import { cn } from '@/lib/utils';
import { TargetType } from '@/types/TargetType.ts';
import { PersonTileVm } from '@/features/dashboard/bookingChanges/types/BillViewModel.ts';
import InitialsAvatar, {
  InitialsAvatarTone,
} from '@/components/ui/InitialsAvatar.tsx';
import { Card } from '@/components/ui/Card.tsx';
import StatusDot from '@/components/ui/StatusDot.tsx';
import { ParticipantStatus } from '@/features/dashboard/participants/types/ParticipantStatus.ts';
import {
  getParticipantStatusLabel,
  PARTICIPANT_STATUS_TONE,
} from '@/features/dashboard/participants/types/ParticipantStatus.utils.ts';
import PersonTileIdentity from './PersonTileIdentity';
import PersonTileOpenButtonWithPrice from './PersonTileOpenButtonWithPrice';

interface PersonTileProps extends React.HTMLAttributes<HTMLDivElement> {
  tile: PersonTileVm;
  isOpen: boolean;
  onToggle: () => void;
  isEditing: boolean;
  /** Osoba, z której ekranu przyszedł operator — subtelnie ciemniejsza ramka. */
  isCurrent?: boolean;
  /** Treść po rozwinięciu: pełny ekran uczestnika albo zapisującego. */
  children?: React.ReactNode;
}

/**
 * Kafel osoby na ekranie rezerwacji. Zwinięty niesie kwotę (rachunek w
 * podglądzie, deltę szkicu w edycji), rozwinięty wpuszcza dokładnie ten sam
 * komponent, co osobny ekran tej osoby.
 *
 * Kafel uczestnika jest większy od kafla zapisującego — uczestnik ma pod sobą
 * rachunek, zapisujący tylko dane kontaktowe.
 */
const PersonTile = ({
  tile,
  isOpen,
  onToggle,
  isEditing,
  isCurrent = false,
  children,
  className,
  ...props
}: PersonTileProps) => {
  const panelId = useId();
  const isParticipant = tile.person.kind === TargetType.PARTICIPANT;
  const isDraftWithoutImpact = Boolean(tile.draftWithoutImpact);

  const personSize = isParticipant ? 'lg' : 'md';

  const avatarTone: InitialsAvatarTone = isParticipant
    ? isCurrent
      ? 'accent'
      : 'plain'
    : 'outline';

  return (
    <Card
      variant={isDraftWithoutImpact ? 'dashed' : 'solid'}
      className={cn(
        'overflow-hidden',
        !isDraftWithoutImpact && isCurrent && 'border-neutral-300',
        className,
      )}
      {...props}
    >
      {/* Nie `<button>`, bo nazwisko jest odnośnikiem, a `<a>` w przycisku to
          nieprawidłowe zagnieżdżenie. Sterowaniem jest przycisk „Rozwiń”. */}
      <div
        onClick={onToggle}
        className={cn(
          'flex w-full cursor-pointer flex-wrap items-center gap-3.5 px-4 text-left sm:px-7',
          isParticipant && isEditing ? 'py-[18px]' : 'py-4',
          isOpen &&
            !isDraftWithoutImpact &&
            'border-b border-neutral-100 bg-neutral-50',
        )}
      >
        <div className="flex min-w-0 flex-1 basis-60 items-center gap-3.5">
          <InitialsAvatar
            initials={tile.person.initials}
            tone={avatarTone}
            size="lg"
            className={cn('size-10', isParticipant ? 'text-[15px]' : 'text-sm')}
          />

          <PersonTileIdentity
            person={tile.person}
            meta={tile.meta}
            isCurrent={isCurrent}
            size={personSize}
            badge={
              tile.withdrawn ? (
                <StatusDot
                  className="text-[13px] text-neutral-500"
                  tone={PARTICIPANT_STATUS_TONE[ParticipantStatus.WITHDRAWN]}
                  label={getParticipantStatusLabel(ParticipantStatus.WITHDRAWN)}
                />
              ) : undefined
            }
          />
        </div>

        <PersonTileOpenButtonWithPrice
          tile={tile}
          isOpen={isOpen}
          isEditing={isEditing}
          size={personSize}
          panelId={panelId}
          onToggle={onToggle}
        />
      </div>

      {isOpen && (
        <div
          id={panelId}
          className={cn(
            isDraftWithoutImpact &&
              'border-t border-dashed border-neutral-300 bg-basic-white',
          )}
        >
          {children}
        </div>
      )}
    </Card>
  );
};

export default PersonTile;
